import { useState } from 'react';
import { QueryObserverResult, RefetchOptions, UseMutateFunction } from '@tanstack/react-query';

import ReadDevicesCommand from 'features/device/domain/models/read-devices-command';
import ReadDevicesResponse from 'features/device/domain/models/read-devices-response';
import ReadFilterValuesResponse from 'features/device/domain/models/read-filter-values-response';
import { useReadDevices, useReadFilterValues } from 'features/device/domain/queries/device-queries';

export type DevicesState = {
  isPopupOpen: boolean;
  allowUnlinking: boolean;
  openPopup: (allowUnlinking?: boolean) => void;
  closePopup: () => void;
  readDevicesData: ReadDevicesResponse | undefined;
  readDevicesIsSuccess: boolean;
  readDevicesIsLoading: boolean;
  readDevicesError: Error | null;
  triggerReadDevicesQuery: UseMutateFunction<ReadDevicesResponse, Error, ReadDevicesCommand, unknown>;
  triggerReadFilterValuesQuery: (options?: RefetchOptions | undefined) => Promise<QueryObserverResult<ReadFilterValuesResponse, Error>>;
  readFilterValuesData: ReadFilterValuesResponse | undefined;
  readFilterValuesError: Error | null;
  readFilterValuesIsLoading: boolean;
  readFilterValuesIsSuccess: boolean;
  readDevicesQuery: ReadDevicesCommand;
  updateReadDevicesQuery: (newQuery: ReadDevicesCommand) => void;
};

export const useDevicesState = (): DevicesState => {
  const [isPopupOpen, setIsPopupOpen] = useState<boolean>(false);
  const [allowUnlinking, setAllowUnlinking] = useState<boolean>(false);

  const [readDevicesQuery, setReadDevicesQuery] = useState<ReadDevicesCommand>({
    pagination: {
      skip: 0,
      take: 10,
    },
    searchQuery: '',
    filters: {
      filterValues: [],
      monitoringFilterValues: [],
    },
    sort: {
      property: 'name',
      isAscending: true,
    },
  });

  const updateReadDevicesQuery = (newQuery: ReadDevicesCommand): void => {
    setReadDevicesQuery(newQuery);
    triggerReadDevicesQuery(newQuery);
    triggerReadFilterValuesQuery(undefined);
  };

  const {
    data: readDevicesData,
    isSuccess: readDevicesIsSuccess,
    isPending: readDevicesIsLoading,
    error: readDevicesError,
    mutate: triggerReadDevicesQuery,
  } = useReadDevices();

  const {
    data: readFilterValuesData,
    error: readFilterValuesError,
    isPending: readFilterValuesIsLoading,
    isSuccess: readFilterValuesIsSuccess,
    refetch: triggerReadFilterValuesQuery,
  } = useReadFilterValues();

  const openPopup = (allowUnlinking: boolean = false): void => {
    setAllowUnlinking(allowUnlinking);
    setIsPopupOpen(true);
  };

  const closePopup = (): void => {
    setIsPopupOpen(false);
    setAllowUnlinking(false);
  };

  return {
    isPopupOpen,
    allowUnlinking,
    openPopup,
    closePopup,
    readDevicesData,
    readDevicesIsSuccess,
    readDevicesIsLoading,
    readDevicesError,
    triggerReadDevicesQuery,
    triggerReadFilterValuesQuery,
    readFilterValuesData,
    readFilterValuesError,
    readFilterValuesIsLoading,
    readFilterValuesIsSuccess,
    readDevicesQuery,
    updateReadDevicesQuery,
  };
};
